import logger from '@widgets/logger';
import { accountConstants } from '@constants/account';
import { accountService } from '@services';
import { getDifferenceInDates } from '@helpers/dates';
import { updateAccountSubscriptions } from '@helpers/account';
import { BILLING_TYPE_MAP } from '@constants/billing';
import { getCurrentSubscription } from '@helpers/billing';

const getBillingDiffInSeconds = data => {
    const mainSubscription = getCurrentSubscription(data);
    if (!mainSubscription) {
        return data.subscriptions?.length ? -1 : 0;
    }
    const expiredAtDate = new Date(mainSubscription.expiresAt);
    if (mainSubscription.billingPlanName !== BILLING_TYPE_MAP.TRIAL) {
        expiredAtDate.setDate(expiredAtDate.getDate() + 30);
    }
    const { billingDiffInSeconds } = getDifferenceInDates(expiredAtDate);
    return data.onboardingCompleted ? billingDiffInSeconds : 0;
};

export const accountActions = {
    get,
    updateAccount,
    checkSubscriptionType,
    getDashboardOnboarding,
    getLocations,
    getPulseAiCredits,
    getDegrees,
};

function get() {
    return dispatch => {
        dispatch(request());
        return accountService
            .getAccount()
            .then(
                data => {
                    const billingDiffInSeconds = getBillingDiffInSeconds(data);

                    const subscriptions = updateAccountSubscriptions(data.subscriptions, data.onboardingCompleted);
                    dispatch(
                        success({
                            ...data,
                            subscriptions,
                            billingDiffInSeconds,
                            // INFO: because of the way the backend works, we need to set the max count to infinity if it's -1
                            companiesMaxCount: data.companiesMaxCount === -1 ? Infinity : data.companiesMaxCount,
                            usersMaxCount: data.usersMaxCount === -1 ? Infinity : data.usersMaxCount,
                        }),
                    );
                    return data;
                },
                error => {
                    dispatch(failure(error.message ? error.message : error));
                },
            )
            .catch(error => dispatch(failure(error)));
    };
}

function updateAccount(account) {
    return dispatch => {
        return accountService.updateAccount(account).then(
            data => {
                const billingDiffInSeconds = getBillingDiffInSeconds(data);

                const subscriptions = updateAccountSubscriptions(data.subscriptions, data.onboardingCompleted);
                dispatch(
                    success({
                        ...data,
                        subscriptions,
                        billingDiffInSeconds,
                        // INFO: because of the way the backend works, we need to set the max count to infinity if it's -1
                        companiesMaxCount: data.companiesMaxCount === -1 ? Infinity : data.companiesMaxCount,
                        usersMaxCount: data.usersMaxCount === -1 ? Infinity : data.usersMaxCount,
                    }),
                );
            },
            error => {
                dispatch(failure(error.message ? error.message : error));
            },
        );
    };
}

function request() {
    return { type: accountConstants.ACCOUNT_REQUEST };
}

function success(account) {
    return { type: accountConstants.ACCOUNT_SUCCESS, account };
}

function failure(message) {
    return { type: accountConstants.ACCOUNT_ERROR, message };
}

function checkSubscriptionType(sessionId) {
    return () => {
        return accountService.checkSubscriptionType(sessionId).then(
            data => {
                return data;
            },
            () => logger.error("Couldn't check subscription type"),
        );
    };
}

function successDashboardOnboarding(data) {
    return { type: accountConstants.DASHBOARD_ONBOARDING_SUCCESS, data };
}

function failureDashboardOnboarding() {
    return { type: accountConstants.DASHBOARD_ONBOARDING_ERROR };
}

function getDashboardOnboarding() {
    return dispatch => {
        accountService.getDashboardOnboarding().then(
            data => dispatch(successDashboardOnboarding(data)),
            () => dispatch(failureDashboardOnboarding()),
        );
    };
}

function locationsRequest() {
    return { type: accountConstants.LOCATIONS_FETCH };
}

function locationsSuccess(data) {
    return { type: accountConstants.LOCATIONS_SUCCESS, data };
}

function locationsFailure() {
    return { type: accountConstants.LOCATIONS_ERROR };
}

function getLocations(query, page, pageSize) {
    return dispatch => {
        dispatch(locationsRequest());
        accountService.getLocations(query, page, pageSize).then(
            data => dispatch(locationsSuccess(data)),
            () => dispatch(locationsFailure()),
        );
    };
}

function successPulseAiCredits(data) {
    return { type: accountConstants.PULSE_AI_CREDITS_SUCCESS, data };
}

function failurePulseAiCredits() {
    return { type: accountConstants.PULSE_AI_CREDITS_ERROR };
}

function getPulseAiCredits() {
    return dispatch => {
        accountService.getPulseAiCredits().then(
            data => {
                const subscriptions = updateAccountSubscriptions(data.subscriptions, data.onboardingCompleted);
                dispatch(successPulseAiCredits({ ...data, subscriptions }));
                return data;
            },
            () => dispatch(failurePulseAiCredits()),
        );
    };
}

function successDegrees(data) {
    return { type: accountConstants.DEGREES_SUCCESS, payload: data };
}

function failureDegrees() {
    return { type: accountConstants.DEGREES_ERROR };
}

function getDegrees() {
    return dispatch => {
        accountService.getDegrees().then(
            data => dispatch(successDegrees(data)),
            () => dispatch(failureDegrees()),
        );
    };
}
